import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, getExpenses, createExpense, deleteExpense, updateTrip } from '../services/firebaseService';
import { Plus, ArrowLeft, Users, DollarSign, Calendar, Calculator, Home, RefreshCw, Download } from 'lucide-react';
import logo from '../assets/logo.png';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const TripDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showSettleUp, setShowSettleUp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchTripData = async () => {
    try {
      console.log('🔍 Fetching trip details for ID:', id);
      console.log('🔄 Component mounted/updated, resetting state');
      
      // Reset state before fetching
      setLoading(true);
      setTrip(null);
      setExpenses([]);
      
      const tripData = await getTrip(id);
      console.log('✅ Trip data fetched:', tripData);
      console.log('📋 Trip data keys:', Object.keys(tripData || {}));
      console.log('🏷️ Trip name fields:', {
        name: tripData?.name,
        title: tripData?.title,
        tripName: tripData?.tripName,
        currency: tripData?.currency
      });
      setTrip(tripData);
      
      console.log('💰 Fetching expenses for trip:', id);
      const expensesData = await getExpenses(id);
      console.log('💸 Expenses data fetched:', expensesData);
      console.log('📊 Number of expenses:', expensesData.length);
      setExpenses(expensesData);
    } catch (error) {
      console.error('❌ Error fetching trip data:', error);
      console.error('Error details:', error.code, error.message);
      // Reset state on error
      setTrip(null);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!id) return;
    
    setRefreshing(true);
    try {
      console.log('🔄 Manually refreshing trip data...');
      await fetchTripData();
      console.log('✅ Trip data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing trip data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchTripData();
    }
  }, [id]);

  const handleAddExpense = async (expenseData) => {
    try {
      console.log('💰 Adding new expense:', expenseData);
      
      // Create the expense with proper structure
      const newExpense = {
        tripId: id,
        description: expenseData.description,
        amount: parseFloat(expenseData.amount),
        category: expenseData.category,
        paidBy: expenseData.paidBy,
        splitType: expenseData.splitType,
        members: expenseData.splitType === 'equal' ? trip.members : expenseData.members,
        date: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
      
      console.log('🔥 Final expense data to save:', newExpense);
      
      const expenseId = await createExpense(newExpense);
      console.log('✅ Expense created with ID:', expenseId);
      
      // Verify the expense was actually saved by trying to fetch it
      console.log('🔍 Verifying expense was saved...');
      const allExpenses = await getExpenses(trip.id);
      console.log('📊 All expenses after save:', allExpenses);
      
      setShowAddExpense(false);
      fetchTripData();
    } catch (error) {
      console.error('❌ Error adding expense:', error);
      alert('Failed to add expense. Please try again.');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }
    
    try {
      console.log('🗑️ Deleting expense:', expenseId);
      await deleteExpense(expenseId);
      console.log('✅ Expense deleted successfully');
      fetchTripData();
    } catch (error) {
      console.error('❌ Error deleting expense:', error);
      alert('Failed to delete expense. Please try again.');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Food & Drinks': '🍽️',
      'Transport': '🚗',
      'Accommodation': '🏨',
      'Entertainment': '🎬',
      'Shopping': '🛍️',
      'Others': '📦'
    };
    return icons[category] || '📦';
  };

  const getDisplayName = (email) => {
    if (!email) return 'Unknown';
    const name = email.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const calculateTotalSpent = () => {
    return expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
  };

  const calculatePerPerson = () => {
    if (trip.members.length === 0) return 0;
    return calculateTotalSpent() / trip.members.length;
  };

  const calculateBalances = () => {
    const balances = {};
    
    // Initialize balances for all members
    trip.members.forEach(member => {
      balances[member] = {
        paid: 0,
        share: 0,
        net: 0
      };
    });

    // Calculate what each person paid and their share
    expenses.forEach(expense => {
      const amount = Number(expense.amount);
      const perPerson = amount / trip.members.length;
      
      // Add to paid amount
      if (balances[expense.paidBy]) {
        balances[expense.paidBy].paid += amount;
      }
      
      // Add to share for all members
      trip.members.forEach(member => {
        if (balances[member]) {
          balances[member].share += perPerson;
        }
      });
    });

    // Calculate net balance
    Object.keys(balances).forEach(member => {
      balances[member].net = balances[member].paid - balances[member].share;
    });

    return balances;
  };

  // Add Expense Modal Component
  const AddExpenseModal = ({ trip, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      description: '',
      amount: '',
      category: 'Food & Drinks',
      paidBy: trip.members[0],
      splitType: 'equal',
      members: trip.members
    });

    const categories = [
      'Food & Drinks',
      'Transport',
      'Accommodation',
      'Entertainment',
      'Shopping',
      'Others'
    ];

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="relative bg-white/95 backdrop-blur-2xl rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 border border-white/20">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Add Expense</h3>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  required
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  className="w-full px-3 py-2 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paid By</label>
                <select
                  value={formData.paidBy}
                  onChange={(e) => setFormData({...formData, paidBy: e.target.value})}
                  className="w-full px-3 py-2 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800"
                >
                  {trip.members.map(member => (
                    <option key={member} value={member}>{member}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-white/20 backdrop-blur border border-white/30 text-gray-800 rounded-xl hover:bg-white/30 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-gradient-to-r from-green-800 via-green-700 to-green-800 text-white font-bold rounded-xl hover:from-green-900 hover:via-green-800 hover:to-green-900 transition-all duration-300"
              >
                Add Expense
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Settle Up Modal Component
  const SettleUpModal = ({ trip, balances, onClose }) => {
    const calculateSettlements = () => {
      const debtors = [];
      const creditors = [];

      Object.entries(balances).forEach(([member, balance]) => {
        if (balance.net > 0) {
          creditors.push({ name: member, amount: balance.net });
        } else if (balance.net < 0) {
          debtors.push({ name: member, amount: Math.abs(balance.net) });
        }
      });

      const settlements = [];
      let debtorIndex = 0;
      let creditorIndex = 0;

      while (debtorIndex < debtors.length && creditorIndex < creditors.length) {
        const debtor = debtors[debtorIndex];
        const creditor = creditors[creditorIndex];
        const amount = Math.min(debtor.amount, creditor.amount);

        settlements.push({
          from: debtor.name,
          to: creditor.name,
          amount: amount
        });

        debtor.amount -= amount;
        creditor.amount -= amount;

        if (debtor.amount === 0) debtorIndex++;
        if (creditor.amount === 0) creditorIndex++;
      }

      return settlements;
    };

    const settlements = calculateSettlements();

    const exportToPDF = async () => {
      try {
        // Create a temporary container for the PDF content
        const pdfContainer = document.createElement('div');
        pdfContainer.style.position = 'absolute';
        pdfContainer.style.left = '-9999px';
        pdfContainer.style.top = '0';
        pdfContainer.style.width = '800px';
        pdfContainer.style.padding = '40px';
        pdfContainer.style.backgroundColor = 'white';
        pdfContainer.style.fontFamily = 'Arial, sans-serif';
        
        // Generate HTML content for PDF
        pdfContainer.innerHTML = `
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #166534; font-size: 28px; margin-bottom: 10px;">Settlement Report</h1>
            <h2 style="color: #374151; font-size: 20px; margin-bottom: 5px;">${trip.name}</h2>
            <p style="color: #6b7280; font-size: 14px;">Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h3 style="color: #374151; font-size: 18px; margin-bottom: 15px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px;">Current Balances</h3>
            ${Object.entries(balances).map(([member, balance]) => `
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; margin-bottom: 8px; background-color: #f9fafb; border-radius: 8px;">
                <div style="display: flex; align-items: center;">
                  <div style="width: 12px; height: 12px; border-radius: 50%; margin-right: 12px; background-color: ${balance.net > 0 ? '#10b981' : balance.net < 0 ? '#ef4444' : '#6b7280'};"></div>
                  <span style="font-weight: 600; color: #111827;">${getDisplayName(member)}</span>
                </div>
                <div style="text-align: right;">
                  <div style="font-weight: 700; color: ${balance.net > 0 ? '#059669' : balance.net < 0 ? '#dc2626' : '#374151'}; font-size: 16px;">
                    ${balance.net > 0 ? '+' : ''}${trip.currency} ${balance.net.toFixed(2)}
                  </div>
                  <div style="font-size: 12px; color: #6b7280;">
                    Paid: ${trip.currency} ${balance.paid.toFixed(2)} • Share: ${trip.currency} ${balance.share.toFixed(2)}
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
          
          ${settlements.length > 0 ? `
            <div>
              <h3 style="color: #374151; font-size: 18px; margin-bottom: 15px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px;">Recommended Settlements</h3>
              ${settlements.map((settlement, index) => `
                <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px; margin-bottom: 10px; background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px;">
                  <div style="display: flex; align-items: center;">
                    <div style="width: 24px; height: 24px; background-color: #16a34a; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; margin-right: 12px;">
                      ${index + 1}
                    </div>
                    <span style="font-weight: 600; color: #111827;">${settlement.from}</span>
                    <span style="margin: 0 8px; color: #6b7280;">→</span>
                    <span style="font-weight: 600; color: #111827;">${settlement.to}</span>
                  </div>
                  <div style="font-weight: 700; color: #059669; font-size: 16px;">
                    ${trip.currency} ${settlement.amount.toFixed(2)}
                  </div>
                </div>
              `).join('')}
            </div>
          ` : `
            <div style="text-align: center; padding: 40px; background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 12px;">
              <div style="font-size: 48px; margin-bottom: 16px;">✅</div>
              <h3 style="color: #166534; font-size: 20px; margin-bottom: 8px;">All Settled Up!</h3>
              <p style="color: #374151;">Everyone has already settled their expenses.</p>
            </div>
          `}
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
            Generated by TravelSplit - Expense Splitting Made Easy
          </div>
        `;

        document.body.appendChild(pdfContainer);

        // Generate canvas from HTML
        const canvas = await html2canvas(pdfContainer, {
          scale: 2,
          useCORS: true,
          logging: false
        });

        // Remove temporary container
        document.body.removeChild(pdfContainer);

        // Create PDF
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        // Save PDF
        pdf.save(`${trip.name}_settlement_report_${new Date().toISOString().split('T')[0]}.pdf`);
        
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Failed to generate PDF. Please try again.');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="relative bg-white/95 backdrop-blur-2xl rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto border border-white/20">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Settle Up</h3>
            <button
              onClick={exportToPDF}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 text-white font-bold rounded-xl hover:from-blue-700 hover:via-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </button>
          </div>
          
          {settlements.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">All Settled Up!</h4>
              <p className="text-gray-700">Everyone has already settled their expenses.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white/20 backdrop-blur rounded-xl p-4 border border-white/30">
                <h4 className="font-medium text-gray-800 mb-3">Recommended Settlements</h4>
                <div className="space-y-3">
                  {settlements.map((settlement, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50/80 backdrop-blur border border-green-200/50 rounded-xl">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-800">{settlement.from}</span>
                        <span className="mx-2 text-gray-600">→</span>
                        <span className="font-medium text-gray-800">{settlement.to}</span>
                      </div>
                      <div className="font-semibold text-green-600">
                        {trip.currency} {settlement.amount.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white font-bold rounded-xl hover:from-gray-900 hover:via-gray-800 hover:to-gray-900 transition-all duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Premium Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-olive-900 via-olive-800 to-emerald-900">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}></div>
          </div>
        </div>
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-olive-600 mx-auto"></div>
            <p className="mt-4 text-gray-200">Loading trip...</p>
          </div>
        </div>
      </div>
    );
  }

  const balances = calculateBalances();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Premium Background with Textures */}
      <div className="absolute inset-0 bg-gradient-to-br from-olive-900 via-olive-800 to-emerald-900">
        {/* Texture Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Gradient Overlay for Depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-olive-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-emerald-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-600/20 rounded-full blur-2xl animate-pulse delay-500"></div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Premium Header */}
        <header className="bg-white/10 backdrop-blur-2xl shadow-2xl border-b border-white/20 relative z-[9999998]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center text-gray-800 hover:text-gray-900 transition-colors font-medium"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Back
                </button>
                <div className="h-8 w-px bg-gradient-to-b from-olive-200 to-green-200"></div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-olive-600 to-green-600 rounded-xl flex items-center justify-center shadow-lg border border-white/20">
                    <img src={logo} alt="TravelSplit" className="w-8 h-8 object-contain" />
                  </div>
                </div>
              </div>
              
              {/* Centered Trip Name */}
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <h1 className="text-3xl font-black text-black tracking-wide" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.05em' }}>
                  {trip?.name || 'Loading...'}
                </h1>
              </div>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing || loading}
                  className="flex items-center px-3 py-2 text-sm bg-white/20 backdrop-blur border border-white/30 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50"
                  title="Refresh trip data"
                >
                  <RefreshCw className={`w-4 h-4 text-olive-200 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center text-gray-800 hover:text-gray-900 transition-colors font-medium"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Dashboard
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Trip Overview */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-30"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-8 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
              <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-600 to-teal-600 rounded-xl mx-auto mb-3 shadow-xl">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-sm text-gray-700 font-medium">Total Spent</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {trip.currency} {calculateTotalSpent().toFixed(2)}
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-olive-600 to-green-600 rounded-xl mx-auto mb-3 shadow-xl">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-sm text-gray-700 font-medium">Members</p>
                    <p className="text-2xl font-bold text-gray-900">{trip.members.length}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl mx-auto mb-3 shadow-xl">
                      <Calculator className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-sm text-gray-700 font-medium">Per Person</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {trip.currency} {calculatePerPerson().toFixed(2)}
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl mx-auto mb-3 shadow-xl">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-sm text-gray-700 font-medium">Expenses</p>
                    <p className="text-2xl font-bold text-gray-900">{expenses.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mt-12 mb-12">
            <button
              onClick={() => setShowAddExpense(true)}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-green-800 via-green-700 to-green-800 text-white font-bold rounded-xl hover:from-green-900 hover:via-green-800 hover:to-green-900 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Expense
            </button>
            <button
              onClick={() => setShowSettleUp(true)}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white font-bold rounded-xl hover:from-gray-900 hover:via-gray-800 hover:to-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Settle Up
            </button>
          </div>

          {/* Members and Balances */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Members List */}
            <div className="relative h-full">
              <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-6 overflow-hidden h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
                <div className="relative h-full flex flex-col">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Members</h2>
                  <div className="space-y-3 flex-1">
                    {trip.members.map((member, index) => (
                      <div key={member} className="flex items-center justify-between p-3 bg-white/15 backdrop-blur border border-white/20 rounded-xl">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-r from-olive-600 to-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                            {getDisplayName(member).charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-800">{getDisplayName(member)}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          {balances[member] && (
                            <span className={`font-semibold ${
                              balances[member].net > 0 ? 'text-green-600' : 
                              balances[member].net < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {balances[member].net > 0 ? '+' : ''}{trip.currency} {balances[member].net.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Balances */}
            <div className="relative h-full">
              <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-6 overflow-hidden h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
                <div className="relative h-full flex flex-col">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Balances</h2>
                  <div className="space-y-3 flex-1">
                    {Object.entries(balances).map(([member, balance]) => (
                      <div key={member} className="flex items-center justify-between p-3 bg-white/15 backdrop-blur border border-white/20 rounded-xl">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            balance.net > 0 ? 'bg-green-500' : 
                            balance.net < 0 ? 'bg-red-500' : 'bg-gray-400'
                          }`}></div>
                          <span className="font-medium text-gray-800">{getDisplayName(member)}</span>
                        </div>
                        <div className="text-right">
                          <div className={`font-semibold ${
                            balance.net > 0 ? 'text-green-600' : 
                            balance.net < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {balance.net > 0 ? '+' : ''}{trip.currency} {balance.net.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-700">
                            Paid: {trip.currency} {balance.paid.toFixed(2)} • Share: {trip.currency} {balance.share.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Expenses */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-30"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-6 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
              <div className="relative">
                <h2 className="text-2xl font-black text-black mb-4">
                  {trip?.name || trip?.title || trip?.tripName || 'Trip'} Expenses
                </h2>
                <div className="space-y-3">
                  {expenses.slice(0, 5).map((expense) => (
                    <div key={expense.id} className="flex items-center justify-between p-3 bg-white/15 backdrop-blur border border-white/20 rounded-xl hover:bg-white/25 transition-all">
                      <div className="flex items-center">
                        <div className="text-2xl mr-3">
                          {getCategoryIcon(expense.category)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-800">{expense.description}</div>
                          <div className="text-sm text-gray-700">
                            Paid by {getDisplayName(expense.paidBy)} • {expense.category}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {trip.currency} {Number(expense.amount).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-700">
                          {expense.date ? (expense.date instanceof Date ? expense.date.toLocaleDateString() : new Date(expense.date).toLocaleDateString()) : 'No date'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* All Expenses */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-30"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-6 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
              <div className="relative">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">All Expenses</h2>
                {expenses.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-700 mb-4">No expenses added yet</p>
                    <button
                      onClick={() => setShowAddExpense(true)}
                      className="px-4 py-2 bg-gradient-to-r from-green-800 via-green-700 to-green-800 text-white font-bold rounded-xl hover:from-green-900 hover:via-green-800 hover:to-green-900 transition-all duration-300"
                    >
                      Add First Expense
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/20">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-800">Description</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-800">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-800">Category</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-800">Paid By</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-800">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-800">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenses.map((expense, index) => (
                          <tr key={expense.id} className="border-b border-white/10 hover:bg-white/10">
                            <td className="py-3 px-4 text-gray-800">{expense.description}</td>
                            <td className="py-3 px-4 font-semibold text-gray-900">
                              {trip.currency} {Number(expense.amount).toFixed(2)}
                            </td>
                            <td className="py-3 px-4">
                              <span className="flex items-center text-gray-800">
                                <span className="mr-2">{getCategoryIcon(expense.category)}</span>
                                {expense.category}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-800">{getDisplayName(expense.paidBy)}</td>
                            <td className="py-3 px-4 text-gray-800">{expense.date ? (expense.date instanceof Date ? expense.date.toLocaleDateString() : new Date(expense.date).toLocaleDateString()) : 'No date'}</td>
                            <td className="py-3 px-4">
                              <button
                                onClick={() => handleDeleteExpense(expense.id)}
                                className="text-red-600 hover:text-red-700 text-sm font-medium"
                                title="Delete expense"
                              >
                                🗑️ Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add Expense Modal */}
        {showAddExpense && (
          <AddExpenseModal
            trip={trip}
            onClose={() => setShowAddExpense(false)}
            onSave={handleAddExpense}
          />
        )}

        {/* Settle Up Modal */}
        {showSettleUp && (
          <SettleUpModal
            trip={trip}
            balances={balances}
            onClose={() => setShowSettleUp(false)}
          />
        )}
      </div>
    </div>
  );
};

export default TripDetails;
